# To create a new validation type please add to both validation_types using the validation name from config and create a method for that validation
# To create a new conditional requirement please add to both required_logic Object and create a method that watches the conditional field and evaluates

# MAJOR CHANGES FROM customValidation:
# model.parameter can no longer be used as a single string, please create a key within parameter (aka model.parameter.field)
# If required is true, must have at least one validation of not_null
# validations is now an array
# client side errors must be a map of key(validation name), value(string to display with error) pair

# sample 5_step config
# :product_info:
  # :requested_loan_amount:
  #   :required: true
  #   :validations:
  #     - not_null
  #     - regex_and_within_range
  #     - divisible_by
  #   :parameter:
  #     :characters: "[0-9.,]+"
  #     :min: 3000
  #     :max: 30000
  #     :divisor: 500

# sample page_config
# client_side_errors:
#   not_null: Please enter the desired borrowing amount
#   regex_and_within_range: Please enter an amount between $3,000 and $30,000
#   divisible_by: Please enter an amount that is an increment of $500

angular.module("avant.customer_applications.directives").directive "amountValidation", [
  'AmountHelper', 'AmountPassword', 'ValidationsRequiredHelper',
  (AmountHelper, AmountPassword, ValidationsRequiredHelper) =>
    restrict: "A"
    require: 'ngModel'
    link: ($scope, element, attributes, ngModel) =>

      ###################
      # Watcher Methods #
      ###################
      # creates a watched fields object to not overload watchers
      @watchedFields = {}

      watcherPrefix = =>
        # radio inputs need a watcher for each input
        if attributes.type == "radio"
          "#{ngModel.$name}:#{attributes.value}"
        else
          ngModel.$name

      setupWatcher = (watchedString, watchFunction) =>
        identifier = "#{watcherPrefix()}::#{watchedString}"
        if !@watchedFields[identifier]
          @watchedFields[identifier] = true
          $scope.$watch watchedString, watchFunction

      ##########################
      # Required Logic Methods #
      ##########################
      booleanGate = (model) =>
        setupWatcher("boolean_gates.#{model.boolean_gate}", (gateValue) =>
          if gateValue
            setFormAlert(runValidations(model, ngModel.$modelValue))
          else
            clearValidityAndAlerts(model)
        )
        $scope.boolean_gates[model.boolean_gate]

      conditionalMatch = (reducer, conditions) =>
        reducer conditions, (condition) =>
          conditionalFieldMatches(condition, $scope.$eval(condition.field))

      # conditions either:
      #   a) check for membership in a set of known values
      #   b) check for presence aka not-null
      conditionalFieldMatches = (condition, fieldValue) =>
        if condition.values
          condition.values.includes(fieldValue)
        else
          AmountHelper.notNull(fieldValue)

      setConditionWatchers = (reducer, conditions, model) =>
        conditions.forEach (condition) =>
          setupWatcher condition.field, =>
            if conditionalMatch(reducer, conditions)
              setFormAlert(runValidations(model, ngModel.$modelValue))
            else
              clearValidityAndAlerts(model)

      conditionallyRequired = (reducer) =>
        (model, value) =>
          setConditionWatchers(reducer, model.conditions, model)
          conditionalMatch(reducer, model.conditions)

      ### start of conditionallyRequiredComplex implementation ###

      conditional_operators =
        all: _.all
        any: _.some

      walkConditional = (cond, output) =>
        if cond.operator? && cond.conditions?
          unless conditional_operators[cond.operator]
            console.warn("invalid conditional operator: ", cond.operator)
          for sub_cond in cond.conditions
            walkConditional(sub_cond, output)
        else if cond.field?
          output.push(cond.field)
        else
          console.warn("invalid conditional format: ", cond)
        output

      watchConditional = (model, config) =>
        identifier = "#{watcherPrefix()}::conditional::root"
        return if @watchedFields[identifier]
        @watchedFields[identifier] = true
        for field in walkConditional(config, [])
          setupWatcher field, =>
            if evaluateConditional(config)
              setFormAlert(runValidations(model, ngModel.$modelValue))
            else
              clearValidityAndAlerts(model)

      evaluateConditional = (cond) =>
        if cond.operator? && cond.conditions?
          operator = conditional_operators[cond.operator]
          return false unless operator
          operator cond.conditions, (sub_cond) =>
            evaluateConditional(sub_cond)
        else if cond.field? && cond.values?
          cond.values.includes($scope.$eval(cond.field))
        else if cond.field?
          AmountHelper.notNull($scope.$eval(cond.field))
        else
          false

      conditionallyRequiredComplex = (model, _value) =>
        watchConditional(model, model.conditionally_required_complex)
        evaluateConditional(model.conditionally_required_complex)

      ### end of conditionallyRequiredComplex implementation ###

      required_logic =
        boolean_gate: booleanGate
        all_conditionally_required: conditionallyRequired(_.all)
        any_conditionally_required: conditionallyRequired(_.some)
        conditionally_required_complex: conditionallyRequiredComplex

      # check if field needs validations checked
      shouldRunValidations = (model, value) =>
        return false unless model
        handler = required_logic[model.required]
        if model.required == true
          return true
        else if handler
          handler(model, value)
        else if fieldShownNotNullAndHasValidations(model, value)
          return true
        else
          return false

      # only validate if: there is data, validations and it is shown on the page
      fieldShownNotNullAndHasValidations = (model, value) =>
        shown = if model.shown_on_page_conditions? then shownOnPage(model) else true
        shown && model.validations? && notNull(model, value)

      shownOnPage = (model) =>
        setConditionWatchers(_.all, model.shown_on_page_conditions, model)
        conditionalMatch(_.all, model.shown_on_page_conditions)

      ###################################
      # Validity and Form Alert Methods #
      ###################################
      clearValidityAndAlerts = (model) =>
        setFormAlert(true)
        setValidityToTrue(model)

      setValidityToTrue = (model) =>
        ngModel.$setValidity 'custom', true
        angular.forEach model?.validations, (validation) =>
          ngModel.$setValidity validation, true
        true

      setFormAlert = (validity) =>
        # $dirty and $pristine check to ensure the alerts don't show on load or first interaction b4 submit
        if $scope.form.$dirty and ngModel.$dirty
          if validity then element.removeClass('form__alert') else element.addClass('form__alert')


      ################################
      # Helpers for date validations #
      ################################

      ###* Formats with date-level specificity ###
      FULL_DATE_FORMATS = ['YYYY-MM-DD', 'MM/DD/YYYY']

      ###* Formats with month-level specificity ###
      MONTH_AND_YEAR_FORMATS = ['MM/YYYY']

      ALL_DATE_FORMATS = MONTH_AND_YEAR_FORMATS.concat(FULL_DATE_FORMATS)

      yearsAgoHelper = (years_ago, date, preposition) =>
        valueDate = moment(date, ALL_DATE_FORMATS, true)
        valueDateYearsAgo = valueDate.isValid() && moment().subtract(years_ago, 'years')
        switch preposition
          when 'after'
            valueDateYearsAgo.isAfter(valueDate)
          when 'before'
            valueDateYearsAgo.isBefore(valueDate)

      ##########################################
      # Helpers for state specific validations #
      ##########################################

      checkStateAgeRequirementsMet = (model, date_of_birth, state) =>
        isAgeValid = model.parameter.state_age_requirements.some((age_requirements) =>
          return false unless state in age_requirements.states
          return true unless yearsAgoHelper(age_requirements.age_required, date_of_birth, 'after')
        )
        !isAgeValid

      checkStateLoanAmountExceptions = (model, value, state) =>
        exceptionFound = model.parameter.state_loan_amount_exceptions.some((exception) =>
          return false if state != exception.state
          if exception.boundary == 'maximum'
            value > exception.loan_amount
          else if exception.boundary == 'minimum'
            value < exception.loan_amount
          else
            console.warn("invalid boundary: ", exception.boundary)
            false
        )
        !exceptionFound

      #################################################
      # Validation Methods - please keep alphabetized #
      #################################################
      complexPassword = (model, value) =>
        # pass scoped data through that isn't available to helper
        data =
          email: $scope.$eval('customer.email')
        AmountPassword.complexPasswordConditionsMet(model, value, data)

      date = (_model, value) =>
        moment(value, FULL_DATE_FORMATS, true).isValid()

      divisibleBy = (model, value) =>
        value && value % "#{model.parameter.divisor}" == 0

      email = (model, value) =>
        re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        guest = /@example\.com$/
        !!(value && re.test(value) && !guest.test(value))

      isChecked = (_model, value) =>
        value == true

      # This method extracts the string at `model.parameter.date`, then evaluates
      # that string in the directive's scope. For example, the string may be
      # `person.date_of_birth`, in which case the function will return whether
      # the date in `value` is after the person's date of birth.
      isDateAfter = (model, value) =>
        modelDate = moment($scope.$eval(model.parameter.date), ALL_DATE_FORMATS, true)
        valueDate = moment(value, ALL_DATE_FORMATS, true)

        modelDate.isValid() and valueDate.isValid() and valueDate.isAfter(modelDate)

      # same as isDateAfterModel, but without evaluating a model field
      isAfterConstantDate = (model, value) =>
        modelDate = moment(model.parameter.after_constant_date, ALL_DATE_FORMATS, true)
        valueDate = moment(value, ALL_DATE_FORMATS, true)

        modelDate.isValid() and valueDate.isValid() and valueDate.isAfter(modelDate)

      isBeforeConstantDate = (model, value) =>
        modelDate = moment(model.parameter.before_constant_date, ALL_DATE_FORMATS, true)
        valueDate = moment(value, ALL_DATE_FORMATS, true)

        modelDate.isValid() and valueDate.isValid() and valueDate.isBefore(modelDate)

      isFutureDate = (_model, value) =>
        valueDate = moment(value, ALL_DATE_FORMATS, true)
        valueDate.isValid() && valueDate.isAfter(moment(), 'day')

      # `model` must have a `model.parameter.years_ago` key that is parseable as
      # one of the date formats in `ALL_DATE_FORMATS`
      isLessThanYearsAgo = (model, value) =>
        yearsAgoHelper(model.parameter.years_ago, value, 'before')

      # `model` must have a `model.parameter.years_ago_greater` key that is parseable as
      # one of the date formats in `ALL_DATE_FORMATS`
      isGreaterThanYearsAgo = (model, value) =>
        yearsAgoHelper(model.parameter.years_ago_greater, value, 'after')

      isNotFutureDate = (_model, value) =>
        valueDate = moment(value, ALL_DATE_FORMATS, true)
        valueDate.isValid() && !valueDate.isAfter(moment(), 'day')

      lengthWithinRange = (model, value) =>
        valueLength = value.length
        Boolean(valueLength && valueLength >= model.parameter.min && valueLength <= model.parameter.max)

      # matchingAny is expecting an array of fields, even if it is just one field
      # to match
      matchingAny = (model, value) =>
        isMatching = false
        angular.forEach model.parameter.fields, (field) =>
          if value != null && value != "null" && (!$scope.$eval(field) || $scope.$eval(field) is value)
            isMatching = true
        return isMatching

      monthAndYear = (_model, value) =>
        moment(value, MONTH_AND_YEAR_FORMATS, true).isValid()

      notApplicableValues  = (model, value) =>
        return true unless value in model.parameter.not_applicable_values

      notMatchingAny = (model, value) =>
        not matchingAny(model, value)

      notNull = (model, value) =>
        AmountHelper.notNull(value)

      notNullDropdown = (model, value) =>
        notNull(model, value) and value != 'null'

      notPoBox = (model, value) =>
        # regex needs to match with the following values: 'PO Box 123','P O box 123','P. O. Box 123','P.O.Box 123',
        # 'post box 123','post office box 123','post office 123','P.O.B 123','P.O.B. 123','Post Office Bin 123',
        # 'Postal Code 123','Post Box #123','Postal Box 123','P.O. Box 123','PO. Box 123','P.o box 123','Pobox 123',
        # 'pob 123','pob123','pobox123','p.o. Box123','po-box123','p.o.-box 123','PO-Box 123','p-o-box 123',
        # 'p-o box 123','box 123','Box123','Box-123
        re = /(?:P(?:ost(?:al)?)?[\.\-\s]*(?:(?:O(?:ffice)?[\.\-\s]*)?B(?:ox|in|\b|\d)|o(?:ffice|\b)(?:[-\s]*\d)|code)|box([-\s]|\b)*\d)/i
        !re.test(value)

      notPoBoxUnlessMilitaryAddress = (model, value) =>
        setupWatcher(model.parameter.military_address_field, (isMilitaryAddress) =>
          setFormAlert(runValidations(model, ngModel.$modelValue))
        )
        isMilitaryAddress = $scope.$eval(model.parameter.military_address_field)
        isMilitaryAddress || notPoBox(model, value)

      regex = (model, value) =>
        re = new RegExp(model.parameter.characters)
        re.test(value)

      regexAndWithinRange = (model, value) =>
        re = new RegExp(model.parameter.characters)
        return false unless re.test(value)
        value = parseFloat(value.replace(/[^\d\.]/g, "")) if typeof value == "string"
        value && value >= model.parameter.min && value <= model.parameter.max

      stateAgeRequirementsMet = (model, value) =>
        setupWatcher(model.parameter.state_field, (state) =>
          isAgeValid = checkStateAgeRequirementsMet(model, ngModel.$modelValue, state)
          if isAgeValid
            clearValidityAndAlerts(model)
          else
            setFormAlert(runValidations(model, ngModel.$modelValue))
        )
        applicantState = $scope.$eval(model.parameter.state_field)
        checkStateAgeRequirementsMet(model, value, applicantState)

      stateLoanAmountExceptions = (model, value) =>
        setupWatcher(model.parameter.state_field, (state) =>
          isAmountValid = checkStateLoanAmountExceptions(model, ngModel.$modelValue, state)
          if isAmountValid
            clearValidityAndAlerts(model)
          else
            setFormAlert(runValidations(model, ngModel.$modelValue))
        )
        applicantState = $scope.$eval(model.parameter.state_field)
        checkStateLoanAmountExceptions(model, value, applicantState)


      validation_types =
        complex_password: complexPassword
        date: date
        divisible_by: divisibleBy
        email: email
        is_checked: isChecked
        is_after_constant_date: isAfterConstantDate
        is_before_constant_date: isBeforeConstantDate
        is_date_after: isDateAfter
        is_future_date: isFutureDate
        is_greater_than_years_ago: isGreaterThanYearsAgo
        is_less_than_years_ago: isLessThanYearsAgo
        is_not_future_date: isNotFutureDate
        length_within_range: lengthWithinRange
        matching_any: matchingAny
        month_and_year: monthAndYear
        not_applicable_values: notApplicableValues
        not_matching_any: notMatchingAny
        not_null: notNull
        not_null_dropdown: notNullDropdown
        not_po_box: notPoBox
        not_po_box_unless_military_address: notPoBoxUnlessMilitaryAddress
        regex: regex
        regex_and_within_range: regexAndWithinRange
        state_age_requirements_met: stateAgeRequirementsMet
        state_loan_amount_exceptions: stateLoanAmountExceptions

      ########################
      # Run Validation Logic #
      ########################
      isValid = (model, value, validation) =>
        validation_types[validation](model, value)

      # run through validations - this only sets the highest priority error as false and sets the lower priority errors true.
      runValidations = (model, value) =>
        return true unless model
        valueValid = true
        angular.forEach model.validations, (validation) =>
          if valueValid
            if isValid(model, value, validation)
              ngModel.$setValidity validation, true
            else
              ngModel.$setValidity validation, false
              valueValid = false
          else
            ngModel.$setValidity validation, true
        ngModel.$setValidity 'custom', valueValid
        valueValid

      requirementsConfig = $scope.requirements[attributes.ngModel]

      enforceValidity = (value) =>
        if shouldRunValidations(requirementsConfig, value)
          runValidations(requirementsConfig, value)
        else
          setValidityToTrue(requirementsConfig)

      # When validating masked fields, the parser needs to be at the beginning of
      # the $parsers array, and the formatter needs to be at the end of the
      # $formatters array; otherwise validations can run on the masked values

      # For DOM => model validation
      ngModel.$parsers.unshift (value) =>
        setFormAlert(enforceValidity(value))
        value

      # For model => DOM validation
      ngModel.$formatters.push (value) =>
        enforceValidity(value)
        value

      $scope.$on('customer_application:presubmit:validation', (event) =>
        ValidationsRequiredHelper.writeToDictionary(
          ngModel.$name,
          shouldRunValidations(requirementsConfig, ngModel.$modelValue)
        )
      )
]
